import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import imageCompression from 'browser-image-compression';

export default function LatestCollections() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');
  
  const [formData, setFormData] = useState({
    title: "",
    fabric: "",
    image: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'image' && files && files[0]) {
      const file = files[0];
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = "Collection title is required";
    }
    
    if (!formData.fabric.trim()) {
      newErrors.fabric = "Fabric is required";
    }

    if (!formData.image) {
      newErrors.image = "Please select an image";
    } else {
      // Validate image file type
      if (!formData.image.type.startsWith('image/')) {
        newErrors.image = "Please select a valid image file";
      }
      
      // Validate file size (max 25MB)
      if (formData.image.size > 25 * 1024 * 1024) {
        newErrors.image = "Image file size must be less than 25MB";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('fabric', formData.fabric.trim());
      
      // Compress image before sending
      if (formData.image) {
        try {
          const compressedFile = await imageCompression(formData.image, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true,
            fileType: 'image/webp' // Convert to WebP format
          });
          formDataToSend.append('image', compressedFile, compressedFile.name);
        } catch (err) {
          setErrors({ submit: "Image compression failed. Please try again." });
          setIsSubmitting(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/add_collection.php`, {
        method: "POST",
        body: formDataToSend,
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setConfirmation("Collection added successfully!");
        // Reset form
        setFormData({
          title: "",
          fabric: "",
          image: null
        });
        setImagePreview(null);
        // Clear success message after 1 second
        setTimeout(() => {
          setConfirmation("");
        }, 1000);
      } else {
        setErrors({ submit: data.error || "Failed to add collection. Please try again." });
      }
    } catch (error) {
      setErrors({ submit: "Failed to add collection. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Add Latest Collection</h1>
          <p className="text-gray-600">Enter collection details and upload an image</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Collection Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Collection Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.title ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Enter collection title"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
          </div>

          {/* Fabric */}
          <div>
            <label htmlFor="fabric" className="block text-sm font-medium text-gray-700 mb-1">
              Fabric *
            </label>
            <input
              type="text"
              id="fabric"
              name="fabric"
              value={formData.fabric}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.fabric ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Enter fabric (e.g., Cotton, Silk, Linen)"
            />
            {errors.fabric && <p className="text-red-500 text-sm mt-1">{errors.fabric}</p>}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Collection Image *
            </label>
            <div className="space-y-3">
              {/* Custom File Upload Button */}
              <div className="relative">
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleChange}
                  className="hidden"
                />
                <label
                  htmlFor="image"
                  className={`flex items-center justify-center w-full px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                    errors.image 
                      ? "border-red-300 bg-red-50" 
                      : imagePreview 
                        ? "border-green-300 bg-green-50" 
                        : "border-gray-300 hover:border-blue-400"
                  }`}
                >
                  <div className="text-center">
                    {imagePreview ? (
                      <>
                        <svg className="mx-auto h-8 w-8 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <p className="text-sm font-medium text-green-700">Image Selected</p>
                        <p className="text-xs text-green-600 mt-1">Click to change image</p>
                      </>
                    ) : (
                      <>
                        <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p className="text-sm font-medium text-gray-700">Tap to select image</p>
                        <p className="text-xs text-gray-500 mt-1">Camera or Gallery</p>
                      </>
                    )}
                  </div>
                </label>
              </div>
              
              {errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
              
              {/* Image Preview */}
              {imagePreview && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600 mb-2">Image Preview:</p>
                  <div className="relative w-full max-w-xs">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-auto rounded-lg border border-gray-300 shadow-sm"
                    />
                    {/* Remove image button */}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, image: null }));
                        setImagePreview(null);
                        setErrors(prev => ({ ...prev, image: "" }));
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      title="Remove image"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                Maximum file size: 25MB
              </p>
            </div>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.submit}</p>
            </div>
          )}
          
          {/* Confirmation Message */}
          {confirmation && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-700 text-sm">{confirmation}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isSubmitting ? "Adding Collection..." : "Add Collection"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 sm:flex-none bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 